import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, SquareDashedMousePointer, Download, Crop, Sparkles, Loader2 } from "lucide-react";
import { SEV_META, TYPE_META } from "@/lib/defects";

let _bid = 0;
const uid = () => `b_${Date.now()}_${_bid++}`;

const loadImage = (url) => new Promise((res, rej) => {
  const i = new Image();
  i.crossOrigin = "anonymous";
  i.onload = () => res(i);
  i.onerror = rej;
  i.src = url;
});

const triggerDownload = (canvas, name) => {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }, "image/jpeg", 0.92);
};

// Draw one or more bounding boxes over a defect photo (Hammer-Missions style).
// Coordinates are stored normalized (0-1) so they survive any resize.
export default function PhotoAnnotator({ open, onOpenChange, photoUrl, boxes = [], defaultType = "crack", defaultSeverity = "medium", assetType = "parking", onSave }) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const startRef = useRef(null);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);

  useEffect(() => {
    if (open) { setItems(boxes.map((b) => ({ ...b }))); setSelected(null); setDraft(null); startRef.current = null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const norm = (e) => {
    const el = imgRef.current || wrapRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
  };

  const onDown = (e) => {
    if (e.button !== 0) return;
    const p = norm(e);
    if (!p) return;
    startRef.current = p;
    setDraft({ x: p.x, y: p.y, w: 0, h: 0 });
    setSelected(null);
  };
  const onMove = (e) => {
    if (!startRef.current) return;
    const p = norm(e);
    if (!p) return;
    const s = startRef.current;
    setDraft({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onUp = () => {
    let created = null;
    if (draft && draft.w > 0.02 && draft.h > 0.02) {
      created = { id: uid(), ...draft, defect_type: defaultType, severity: defaultSeverity };
      setItems((prev) => [...prev, created]);
      setSelected(created.id);
    }
    startRef.current = null;
    setDraft(null);
    if (created) identifyBox(created);
  };

  const update = (id, patch) => setItems((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => { setItems((prev) => prev.filter((b) => b.id !== id)); if (selected === id) setSelected(null); };

  const cropToDataUrl = async (b) => {
    const img = await loadImage(photoUrl);
    const W = img.naturalWidth, H = img.naturalHeight;
    const sw = Math.max(1, Math.round(b.w * W)), sh = Math.max(1, Math.round(b.h * H));
    const c = document.createElement("canvas");
    c.width = sw; c.height = sh;
    c.getContext("2d").drawImage(img, b.x * W, b.y * H, sw, sh, 0, 0, sw, sh);
    return c.toDataURL("image/jpeg", 0.9);
  };

  const identifyBox = async (b) => {
    setAnalyzingId(b.id);
    try {
      const dataUrl = await cropToDataUrl(b);
      const { data } = await api.post("/ai/classify-defect", { image_base64: dataUrl, asset_type: assetType });
      update(b.id, { defect_type: data.defect_type || b.defect_type, severity: data.severity || b.severity, label: data.title || undefined, ai: data });
      toast.success(`Identified: ${data.title || TYPE_META[data.defect_type]?.label || "defect"}`);
    } catch {
      toast.error("Could not identify — set the type manually");
    } finally {
      setAnalyzingId(null);
    }
  };

  const downloadCrop = async (b) => {
    try {
      const img = await loadImage(photoUrl);
      const W = img.naturalWidth, H = img.naturalHeight;
      const sw = Math.max(1, Math.round(b.w * W)), sh = Math.max(1, Math.round(b.h * H));
      const c = document.createElement("canvas");
      c.width = sw; c.height = sh;
      c.getContext("2d").drawImage(img, b.x * W, b.y * H, sw, sh, 0, 0, sw, sh);
      triggerDownload(c, `defect-${b.defect_type}-${b.severity}.jpg`);
    } catch { /* cross-origin/load issue */ }
  };

  const downloadAll = async () => {
    try {
      const img = await loadImage(photoUrl);
      const W = img.naturalWidth, H = img.naturalHeight;
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const bw = Math.max(4, Math.round(Math.min(W, H) * 0.014));
      items.forEach((b) => {
        const x = b.x * W, y = b.y * H, w = b.w * W, h = b.h * H;
        ctx.lineWidth = bw + 4; ctx.strokeStyle = "rgba(0,0,0,0.7)"; ctx.strokeRect(x, y, w, h);
        ctx.lineWidth = bw; ctx.strokeStyle = SEV_META[b.severity]?.color || "#FFCC00"; ctx.strokeRect(x, y, w, h);
      });
      triggerDownload(c, "defect-photo.jpg");
    } catch { /* cross-origin/load issue */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-foreground sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="photo-annotator">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <SquareDashedMousePointer className="w-4 h-4 text-[#C5A24A]" /> Box the defects
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Click and drag a rectangle around each defect — the AI identifies what's inside automatically. Fine-tune type/severity on the right.</p>

        <div className="grid md:grid-cols-[1fr_240px] gap-4">
          <div className="flex items-start justify-center bg-black rounded overflow-hidden">
            <div
              ref={wrapRef}
              onMouseDown={onDown}
              onMouseMove={onMove}
              onMouseUp={onUp}
              onMouseLeave={onUp}
              className="relative inline-block w-fit select-none cursor-crosshair"
              data-testid="annotator-canvas"
            >
              <img ref={imgRef} src={photoUrl} alt="defect" draggable={false} className="block max-h-[62vh] max-w-full pointer-events-none" />
              {items.map((b, i) => (
                <div
                  key={b.id}
                  onMouseDown={(e) => { e.stopPropagation(); setSelected(b.id); }}
                  className={`absolute cursor-pointer ${selected === b.id ? "ring-2 ring-white" : ""}`}
                  style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%`, border: `4px solid ${SEV_META[b.severity]?.color || "#FFCC00"}`, boxShadow: "0 0 0 2px rgba(0,0,0,0.65), inset 0 0 0 1px rgba(0,0,0,0.4)" }}
                >
                  <span className="absolute -top-2.5 -left-2.5 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border border-black/40" style={{ background: "#C5A24A", color: "#000" }} data-testid="annotator-box-badge">
                    {analyzingId === b.id ? "…" : i + 1}
                  </span>
                </div>
              ))}
              {draft && (
                <div className="absolute border-2 border-dashed border-white/90 bg-white/5" style={{ left: `${draft.x * 100}%`, top: `${draft.y * 100}%`, width: `${draft.w * 100}%`, height: `${draft.h * 100}%` }} />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <p className="label-caps text-muted-foreground text-xs">Defects boxed ({items.length})</p>
            {items.length === 0 && <p className="text-xs text-muted-foreground">No boxes yet — drag on the photo to add one.</p>}
            <div className="space-y-2 max-h-[54vh] overflow-y-auto pr-1">
              {items.map((b, i) => (
                <div
                  key={b.id}
                  onClick={() => setSelected(b.id)}
                  data-testid="annotator-box-row"
                  className={`p-2 rounded border ${selected === b.id ? "border-[#C5A24A]" : "border-white/10"} bg-[#0A0A0A] space-y-2`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{ color: SEV_META[b.severity]?.color }}>#{i + 1}</span>
                    <div className="flex items-center gap-2">
                      <button type="button" disabled={analyzingId === b.id} onClick={(e) => { e.stopPropagation(); identifyBox(b); }} data-testid="annotator-box-identify" title="Identify defect with AI" className="text-muted-foreground hover:text-[#D8BE86] disabled:opacity-50">
                        {analyzingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); downloadCrop(b); }} data-testid="annotator-box-download" title="Download this area as a photo" className="text-muted-foreground hover:text-[#D8BE86]">
                        <Crop className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); remove(b.id); }} data-testid="annotator-box-remove" title="Delete box" className="text-muted-foreground hover:text-[#FF3B30]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <Select value={b.defect_type} onValueChange={(v) => update(b.id, { defect_type: v })}>
                    <SelectTrigger className="h-8 bg-[#121212] border-white/10 text-xs" data-testid="annotator-box-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-white/10">{Object.keys(TYPE_META).map((k) => <SelectItem key={k} value={k}>{TYPE_META[k].label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={b.severity} onValueChange={(v) => update(b.id, { severity: v })}>
                    <SelectTrigger className="h-8 bg-[#121212] border-white/10 text-xs" data-testid="annotator-box-severity"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-white/10">{Object.keys(SEV_META).map((k) => <SelectItem key={k} value={k}>{SEV_META[k].label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          {items.length > 0 && (
            <Button type="button" variant="outline" data-testid="annotator-download-all" onClick={downloadAll} className="gap-2 border-white/15 bg-[#0A0A0A] hover:bg-[#1E1E1E]">
              <Download className="w-4 h-4" /> Download photo
            </Button>
          )}
          <Button data-testid="annotator-save" onClick={() => onSave(items)} className="bg-[#C5A24A] hover:bg-[#A8863A] text-black">Save boxes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
